import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, MapPin, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { collectionPointsAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { money } from '@/lib/format';

export default function AdminCollectionPoints() {
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', address: '', description: '', is_active: true, collection_fee: '', collection_commission: '', google_maps_url: '', collection_hours: '' });

  const fetchPoints = () => {
    setLoading(true);
    collectionPointsAPI.listAll()
      .then(r => setPoints(r.data))
      .catch(() => toast.error('Failed to load collection points'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPoints(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', address: '', description: '', is_active: true, collection_fee: '', collection_commission: '', google_maps_url: '', collection_hours: '' });
    setDialogOpen(true);
  };

  const openEdit = (point) => {
    setEditing(point);
    setForm({
      name: point.name, address: point.address, description: point.description || '',
      is_active: point.is_active,
      collection_fee: (point.collection_fee ?? '') === '' ? '' : String(point.collection_fee ?? ''),
      collection_commission: (point.collection_commission ?? '') === '' ? '' : String(point.collection_commission ?? ''),
      google_maps_url: point.google_maps_url || '',
      collection_hours: point.collection_hours || '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.address.trim()) {
      toast.error('Name and address are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, collection_fee: parseFloat(form.collection_fee) || 0, collection_commission: parseFloat(form.collection_commission) || 0 };
      if (editing) {
        await collectionPointsAPI.update(editing.id, payload);
        toast.success('Collection point updated');
      } else {
        await collectionPointsAPI.create(payload);
        toast.success('Collection point created');
      }
      setDialogOpen(false);
      fetchPoints();
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this collection point?')) return;
    try {
      await collectionPointsAPI.delete(id);
      toast.success('Deleted');
      fetchPoints();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="admin-collection-points">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Collection Points</h1>
          <p className="font-body text-sm text-foreground/70 mt-1">{points.length} collection points</p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="add-collection-point-btn"
          className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
        >
          <Plus className="h-4 w-4" /> Add Collection Point
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 rounded-2xl bg-muted animate-pulse" />)}
        </div>
      ) : points.length === 0 ? (
        <div className="text-center py-16 rounded-2xl bg-muted/30 border border-border/50">
          <MapPin className="h-12 w-12 text-foreground/20 mx-auto mb-4" />
          <p className="font-heading text-xl text-foreground/50">No collection points yet</p>
          <p className="font-body text-sm text-foreground/40 mt-1">Add your first collection point above</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="collection-points-list">
          {points.map(point => (
            <div
              key={point.id}
              data-testid={`cp-${point.id}`}
              className="flex items-center gap-4 p-5 rounded-2xl bg-card border border-border hover:border-teal/30 transition-colors"
            >
              <div className="p-2.5 rounded-xl bg-teal/10 flex-shrink-0">
                <MapPin className="h-5 w-5 text-teal" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-body text-base font-semibold text-foreground">{point.name}</h3>
                  <span className={`font-body text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    point.is_active
                      ? 'bg-green-400/15 text-green-400'
                      : 'bg-red-400/15 text-red-400'
                  }`}>
                    {point.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <span
                    data-testid={`cp-fee-${point.id}`}
                    className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-gold/15 text-gold"
                  >
                    {(point.collection_fee || 0) > 0 ? `Fee R${money(Number(point.collection_fee))}` : 'Free'}
                  </span>
                  {(point.collection_commission || 0) > 0 && (
                    <span
                      data-testid={`cp-commission-${point.id}`}
                      className="font-body text-[10px] font-bold px-2 py-0.5 rounded-full bg-orange-400/15 text-orange-400"
                    >
                      Commission R{money(Number(point.collection_commission))}
                    </span>
                  )}
                </div>
                <p className="font-body text-sm text-foreground/60 mt-0.5">{point.address}</p>
                {point.description && (
                  <p className="font-body text-xs text-foreground/40 mt-0.5">{point.description}</p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => openEdit(point)}
                  className="h-8 w-8 hover:bg-muted rounded-full"
                  data-testid={`edit-cp-${point.id}`}
                >
                  <Pencil className="h-3.5 w-3.5 text-foreground/50" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleDelete(point.id)}
                  className="h-8 w-8 hover:bg-red-900/20 rounded-full"
                  data-testid={`delete-cp-${point.id}`}
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-400/60" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-background rounded-2xl max-w-md" data-testid="cp-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-foreground">
              {editing ? 'Edit Collection Point' : 'Add Collection Point'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Name</Label>
              <Input
                data-testid="cp-name-input"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="e.g. Johannesburg Depot"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Address</Label>
              <Input
                data-testid="cp-address-input"
                value={form.address}
                onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="123 Main Rd, Johannesburg"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Google Maps link (optional)</Label>
              <Input
                data-testid="cp-maps-input"
                value={form.google_maps_url}
                onChange={e => setForm(f => ({ ...f, google_maps_url: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="https://maps.app.goo.gl/..."
              />
              <p className="font-body text-[11px] text-foreground/40 mt-1.5">Paste a precise pin from Google Maps (open the place → Share → Copy link). Leave blank to auto-generate a map link from the address.</p>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Collection Fee (ZAR)</Label>
              <Input
                data-testid="cp-fee-input"
                type="number"
                step="0.01"
                min="0"
                value={form.collection_fee}
                onChange={e => setForm(f => ({ ...f, collection_fee: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="0.00"
              />
              <p className="font-body text-[11px] text-foreground/40 mt-1.5">Fixed fee charged once per order collected here. Leave blank or 0 for free collection.</p>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Collection Commission (ZAR)</Label>
              <Input
                data-testid="cp-commission-input"
                type="number"
                step="0.01"
                min="0"
                value={form.collection_commission}
                onChange={e => setForm(f => ({ ...f, collection_commission: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="0.00"
              />
              <p className="font-body text-[11px] text-foreground/40 mt-1.5">Amount you pay this collection point per order. Internal cost only &mdash; never shown on customer invoices or supplier POs. Deducted in your gross profit reports.</p>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Collection Hours (optional)</Label>
              <Input
                data-testid="cp-hours-input"
                value={form.collection_hours}
                onChange={e => setForm(f => ({ ...f, collection_hours: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="Mon–Fri 9am–5pm, Sat 9am–1pm"
              />
              <p className="font-body text-[11px] text-foreground/40 mt-1.5">Shown to customers in the &quot;ready for collection&quot; WhatsApp message.</p>
            </div>
            <div>
              <Label className="font-body text-sm text-foreground/70 font-medium mb-2 block">Description (optional)</Label>
              <Input
                data-testid="cp-desc-input"
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="rounded-xl bg-muted border-border font-body text-foreground"
                placeholder="Near the main entrance..."
              />
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={v => setForm(f => ({ ...f, is_active: v }))}
                data-testid="cp-active-toggle"
              />
              <span className="font-body text-sm text-foreground/60">{form.is_active ? 'Active' : 'Inactive'}</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full border-border font-body text-sm">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="cp-save-btn"
              className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
